import { useNavigate } from 'react-router-dom';
import { FeatureCard } from '@components/ui/cards/FeatureCard';
import { useRequestPayment } from '@features/payments';
import { useSubjectLanguageVariants } from '@features/user';
import { useEventTracking } from '@services/statsig';

import AdminToolsIcon from '../../../assets/admin-tools.svg?react';

import { AdminToolTile } from './AdminToolTile';
import { useCanManagePatientFinancials } from '@features/billing/permissions/useCanManagePatientFinancials';

export const AdminToolsCard = () => {
  const canManagePatientFinancials = useCanManagePatientFinancials();
  const navigate = useNavigate();
  const { singularCap: subjectLanguage } = useSubjectLanguageVariants();
  const { dashboard } = useEventTracking();

  // Initialize the payment request hook for the payment tile
  const { triggerPaymentRequest } = useRequestPayment({
    source: 'dashboard_admin_tile',
  });

  // Admin tool tiles configuration
  const adminToolTiles = [
    {
      id: 'appointment',
      imageSrc: '/dashboard/home-minitile_schedule-appt.png',
      imageAlt: 'Create Appointment',
      buttonText: 'Create Appointment',
      route: '/schedule',
      actionType: 'schedule_appointment',
      customAction: null,
    },
    {
      id: 'form',
      imageSrc: '/dashboard/home-minitile_send-patient-form.png',
      imageAlt: `Send ${subjectLanguage} Form`,
      buttonText: `Send ${subjectLanguage} Form`,
      route: '/forms',
      actionType: 'send_form',
      customAction: null,
    },
    // The tile, not the card (gdn-3287): every other tool here is
    // available to a view-only user, so only this one goes.
    ...(canManagePatientFinancials
      ? [
          {
            id: 'payment',
            imageSrc: '/dashboard/home-minitile_request-payment.png',
            imageAlt: 'Collect payment',
            buttonText: 'Collect payment',
            route: null,
            actionType: 'request_payment',
            customAction: triggerPaymentRequest, // headless hook triggers the flow
          },
        ]
      : []),
  ];

  return (
    <FeatureCard
      icon={<AdminToolsIcon width={20} height={20} fill="currentColor" />}
      title="Explore Administrative Tools"
      fullHeight
      sx={{ gridColumn: { xs: '1', md: 'span 2' } }}
      contentSx={{
        p: 3,
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        flexWrap: 'nowrap',
        gap: 3,
        justifyContent: 'center',
        alignItems: { xs: 'center', sm: 'flex-start' },
      }}
    >
      {adminToolTiles.map(tile => (
        <AdminToolTile
          key={tile.id}
          imageSrc={tile.imageSrc}
          imageAlt={tile.imageAlt}
          buttonText={tile.buttonText}
          onClick={() => {
            dashboard.actionClicked({
              action_type: tile.actionType,
              source: 'dashboard_v2_admin_tile',
            });

            // Execute custom action if available, otherwise navigate
            if (tile.customAction) {
              tile.customAction();
            } else if (tile.route) {
              navigate(tile.route);
            }
          }}
        />
      ))}
    </FeatureCard>
  );
};
